/**
 * useCookieConsent Hook
 *
 * Manages cookie consent state (for cookie acceptance ONLY).
 *
 * @returns {Object} Consent state and handlers
 */
import { useState, useEffect } from "react";

const STORAGE_KEY = "cookieConsent";
const CONSENT_EVENT = "cookieConsentUpdated";

const readStoredConsent = () => {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const useCookieConsent = () => {
  const [consent, setConsent] = useState(null);
  const [shouldShowBanner, setShouldShowBanner] = useState(false);

  useEffect(() => {
    const stored = readStoredConsent();
    if (stored && typeof stored.accepted === "boolean") {
      setConsent(stored.accepted);
      setShouldShowBanner(false);
      return;
    }

    // Default to denied until the user makes a choice.
    setConsent(false);
    setShouldShowBanner(true);
  }, []);

  useEffect(() => {
    const handleOpenSettings = () => {
      setShouldShowBanner(true);
    };

    window.addEventListener("openCookieSettings", handleOpenSettings);
    return () => window.removeEventListener("openCookieSettings", handleOpenSettings);
  }, []);

  useEffect(() => {
    const handleConsentUpdate = (event) => {
      if (event?.detail && typeof event.detail.accepted === "boolean") {
        setConsent(event.detail.accepted);
        setShouldShowBanner(false);
        return;
      }

      const stored = readStoredConsent();
      if (stored && typeof stored.accepted === "boolean") {
        setConsent(stored.accepted);
        setShouldShowBanner(false);
      }
    };

    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEY) return;
      handleConsentUpdate();
    };

    window.addEventListener(CONSENT_EVENT, handleConsentUpdate);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(CONSENT_EVENT, handleConsentUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const acceptCookies = () => {
    const data = { accepted: true, timestamp: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setConsent(true);
    setShouldShowBanner(false);
    window.dispatchEvent(new window.CustomEvent(CONSENT_EVENT, { detail: { accepted: true } }));
  };

  const declineCookies = () => {
    const data = { accepted: false, timestamp: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setConsent(false);
    setShouldShowBanner(false);
    window.dispatchEvent(new window.CustomEvent(CONSENT_EVENT, { detail: { accepted: false } }));
  };

  return { consent, acceptCookies, declineCookies, shouldShowBanner };
};

export default useCookieConsent;
